import { useEffect, useRef, useState, type ReactNode } from "react";
import {
	AppSidebar,
	BrowserChrome,
	ChatMessage,
	type Citation,
	ChatPanel,
	DocumentMention,
	LibraryItem,
} from "@baseline/ui";
import { Ellipsis } from "lucide-react";

// ── Mock data ───────────────────────────────────────────────

const SIDEBAR_RECENT = [
	{ icon: "doc" as const, title: "Competitive Landscape" },
	{ icon: "link" as const, title: "Stratechery — Pricing" },
	{ icon: "doc" as const, title: "Pricing Strategy Notes" },
	{ icon: "video" as const, title: "YC Pricing Workshop" },
	{ icon: "doc" as const, title: "Product Vision Q2" },
];

const SIDEBAR_STARRED = [
	{ icon: "doc" as const, title: "Product Vision Q2" },
	{ icon: "link" as const, title: "a16z Marketplace Guide" },
];

const HERO_CITATIONS: Citation[] = [
	{ id: 1, label: "Stratechery — Pricing" },
	{ id: 2, label: "YC Pricing Workshop" },
	{ id: 3, label: "Elicit analysis note" },
];

const CHAT_CITATIONS: Citation[] = [
	{ id: 1, label: "Profitwell: SaaS Pricing Report" },
	{ id: 2, label: "Stratechery — Aggregation Theory" },
	{ id: 3, label: "Your note — Elicit experiment" },
];

// ── Shared sub-components ───────────────────────────────────

function DocumentTabs({ activeTab = "Document" }: { activeTab?: string }) {
	return (
		<div className="border-cv-bdr flex h-9 shrink-0 items-center gap-5 border-b px-6">
			{["Document", "Sources", "History"].map((tab) => (
				<span
					key={tab}
					className={`pb-1.5 text-[11px] ${
						tab === activeTab
							? "border-cv-accent text-cv-fg border-b"
							: "text-cv-fg-muted"
					}`}
				>
					{tab}
				</span>
			))}
		</div>
	);
}

function DocumentBreadcrumb({ title }: { title: string }) {
	return (
		<div className="border-cv-bdr flex h-11 shrink-0 items-center border-b px-5">
			<div className="flex items-center gap-1.5 text-[13px]">
				<span className="text-cv-fg-muted">Library</span>
				<span className="text-cv-fg-muted">/</span>
				<span className="text-cv-fg">{title}</span>
			</div>
			<div className="ml-auto">
				<button className="border-cv-bdr-mid text-cv-fg-muted flex h-6 w-6 items-center justify-center rounded border text-xs">
					<Ellipsis className="h-3.5 w-3.5" />
				</button>
			</div>
		</div>
	);
}

function SectionHeader({
	headline,
	headlineItalic,
	description,
}: {
	headline: string;
	headlineItalic: string;
	description: string;
}) {
	return (
		<div className="mb-8">
			<h2 className="mb-4 font-serif text-[clamp(36px,5vw,56px)] leading-[1.1] text-[#e8e8e8]">
				{headline}
				<span className="text-[#a0a0a8] italic">{headlineItalic}</span>
			</h2>
			<p className="max-w-2xl text-base leading-relaxed text-[#a0a0a8]">
				{description}
			</p>
		</div>
	);
}

// ── Scaled mock wrapper ─────────────────────────────────────
// Mocks render at a fixed min-width, then scale down continuously to fit
// the container. A ResizeObserver drives the scale factor so the transition
// is smooth at every viewport width — no media-query breakpoints.

function ScaledMock({
	children,
	minWidth = 900,
}: {
	children: ReactNode;
	minWidth?: number;
}) {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const innerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const inner = innerRef.current;
		if (!wrapper || !inner) return;

		const ro = new ResizeObserver(() => {
			const scale = Math.min(1, wrapper.clientWidth / minWidth);
			inner.style.transform = `scale(${scale})`;
			// Collapse the empty space left by the visual scaling
			inner.style.marginBottom =
				scale < 1 ? `${-(inner.scrollHeight * (1 - scale))}px` : "";
		});
		ro.observe(wrapper);
		return () => ro.disconnect();
	}, [minWidth]);

	return (
		<div ref={wrapperRef} style={{ overflow: "hidden" }}>
			<div
				ref={innerRef}
				style={{ minWidth, transformOrigin: "top left" }}
			>
				{children}
			</div>
		</div>
	);
}

// ── Section: Nav ────────────────────────────────────────────

function SoonButton({ className }: { className?: string }) {
	const [clicked, setClicked] = useState(false);
	return (
		<button
			type="button"
			onClick={() => setClicked(true)}
			className={className}
			style={
				clicked
					? {
							background: "#dc2626",
							borderColor: "#dc2626",
							color: "#fff",
						}
					: undefined
			}
		>
			{clicked ? "Soon" : "Get started"}
		</button>
	);
}

function Nav() {
	return (
		<nav className="landing-nav sticky top-0 z-50 flex h-14 items-center border-b border-[rgba(255,255,255,0.08)] bg-[rgba(9,9,11,0.8)] px-6 backdrop-blur-xl md:px-12">
			<div className="mx-auto flex w-full max-w-[1200px] items-center">
				<span
					className="text-[24px] text-[#e8e8e8]"
					style={{ fontFamily: "'Comfortaa', sans-serif" }}
				>
					clearvoid
				</span>
				<div className="ml-auto">
					<SoonButton className="landing-nav-cta rounded-md border border-[rgba(255,255,255,0.12)] px-4 py-1.5 text-sm font-medium text-[#e8e8e8] transition-all hover:border-[rgba(255,255,255,0.3)] hover:bg-[rgba(255,255,255,0.06)]" />
				</div>
			</div>
		</nav>
	);
}

// ── Section: Hero ───────────────────────────────────────────

function Hero() {
	return (
		<section
			className="border-b border-[rgba(255,255,255,0.08)] px-6 py-24 md:px-12"
			style={{
				background:
					"radial-gradient(ellipse 80% 60% at 55% 40%, #1a1a20 0%, transparent 65%), radial-gradient(ellipse 55% 45% at 75% 20%, #161618 0%, transparent 60%), #09090b",
			}}
		>
			<div className="mx-auto max-w-[1200px]">
				<div className="mb-10">
					<h1 className="mb-5 font-serif text-[clamp(48px,6vw,80px)] leading-[1.05] text-[#e8e8e8]">
						Stop losing your best{" "}
						<span className="text-[#a0a0a8] italic">thinking.</span>
					</h1>
					<p className="mb-8 max-w-2xl text-lg leading-relaxed text-[#a0a0a8]">
						Save articles, papers, videos, and your own notes.
						Clearvoid reads everything and lets you chat across all
						of it — so your research compounds instead of
						disappearing.
					</p>
					<div className="mb-3 flex items-center gap-3">
						<SoonButton className="rounded-md bg-white px-5 py-2.5 text-sm font-medium text-[#09090b] transition-opacity hover:opacity-90" />
					</div>
				</div>
				{/* Hero app mock */}
				<ScaledMock>
					<BrowserChrome url="app.clearvoid.com / library / competitive-landscape">
						<div className="flex h-[520px]">
							<AppSidebar
								activeItem="Competitive Landscape"
								recentItems={SIDEBAR_RECENT}
								starredItems={SIDEBAR_STARRED}
							/>
							<div className="flex min-w-0 flex-1 flex-col">
								<DocumentBreadcrumb title="Competitive Landscape" />
								<DocumentTabs />
								<div className="flex-1 overflow-hidden px-8 py-6">
									<div className="text-cv-fg-muted mb-2 text-[10px] font-medium tracking-[0.1em] uppercase">
										Research Document
									</div>
									<div className="text-cv-fg mb-3 font-serif text-2xl">
										Competitive Landscape
									</div>
									<div className="mb-4 flex flex-wrap gap-1.5">
										{[
											"Pricing",
											"SaaS",
											"Research Tools",
											"2026 Q1",
										].map((tag) => (
											<span
												key={tag}
												className="border-cv-bdr-mid text-cv-fg-muted rounded-sm border px-2 py-0.5 text-[9px] font-medium tracking-wide uppercase"
											>
												{tag}
											</span>
										))}
									</div>
									<div className="border-cv-bdr mb-4 border-t" />
									<div className="text-cv-fg-muted mb-1.5 text-[10px] font-semibold tracking-[0.1em] uppercase">
										Overview
									</div>
									<div className="text-cv-fg mb-1.5 font-serif text-sm italic">
										Market positioning and key players
									</div>
									<div className="text-cv-fg-sec mb-4 text-xs leading-relaxed">
										Three categories dominate: note-taking
										tools, AI writing assistants, and
										dedicated research tools. None fully
										address the accumulation problem — the
										gap between reading and using what you
										have read.
										<sup className="text-cv-accent ml-0.5 text-[8px] font-semibold">
											1
										</sup>
									</div>
									<div className="text-cv-fg-muted mb-1.5 text-[10px] font-semibold tracking-[0.1em] uppercase">
										Pricing Models
									</div>
									<div className="text-cv-fg mb-1.5 font-serif text-sm italic">
										Subscription versus usage-based
									</div>
									<div className="text-cv-fg-sec mb-4 text-xs leading-relaxed">
										The core tension is between subscription
										predictability and usage-based fairness.
										Flat monthly pricing systematically has
										power users subsidizing light users.
										<sup className="text-cv-accent ml-0.5 text-[8px] font-semibold">
											2
										</sup>
									</div>
									<div className="border-cv-bdr mt-2 border-t pt-3">
										<div className="text-cv-fg-muted mb-2 text-[9px] font-semibold tracking-[0.12em] uppercase">
											Sources
										</div>
										<div className="mb-1 flex items-baseline gap-2">
											<span className="text-cv-accent text-[9px] font-bold">
												1
											</span>
											<span className="text-cv-fg-sec text-[10px]">
												Stratechery — Pricing Strategies
												in SaaS
											</span>
										</div>
										<div className="flex items-baseline gap-2">
											<span className="text-cv-accent text-[9px] font-bold">
												2
											</span>
											<span className="text-cv-fg-sec text-[10px]">
												YC Pricing Workshop transcript
											</span>
										</div>
									</div>
								</div>
							</div>
							<div className="w-[280px] shrink-0">
								<ChatPanel sourceCount={7}>
									<ChatMessage
										role="user"
										content="What were the main arguments against flat subscription pricing?"
									/>
									<ChatMessage
										role="ai"
										content={
											<>
												Based on your saved sources, the
												core argument is value
												misalignment.
												<sup className="text-cv-accent text-[8px] font-bold">
													1
												</sup>{" "}
												Casual users pay the same as
												power users, capping revenue and
												creating churn at both ends.
												<sup className="text-cv-accent text-[8px] font-bold">
													2
												</sup>{" "}
												Your Elicit note observes they
												resolved this with a credit
												hybrid.
												<sup className="text-cv-accent text-[8px] font-bold">
													3
												</sup>
											</>
										}
										citations={HERO_CITATIONS}
									/>
								</ChatPanel>
							</div>
						</div>
					</BrowserChrome>
				</ScaledMock>
			</div>
		</section>
	);
}

// ── Section: Statement ──────────────────────────────────────

function Statement() {
	return (
		<section className="border-b border-[rgba(255,255,255,0.08)] px-6 py-40 md:px-12">
			<div className="mx-auto max-w-[800px]">
				<p
					className="text-[clamp(24px,3vw,38px)] leading-[1.45] font-light text-[#a0a0a8]"
					style={{ fontFamily: "'Lora', Georgia, serif" }}
				>
					A research tool that{" "}
					<strong className="font-medium text-[#e8e8e8]">
						remembers everything you have saved
					</strong>{" "}
					and helps you think about it. The more you save, the more
					useful every conversation becomes.
				</p>
			</div>
		</section>
	);
}

// ── Section: Feature 1 — Save anything ──────────────────────

function FeatureSave() {
	return (
		<section
			id="features"
			className="border-b border-[rgba(255,255,255,0.08)] px-6 py-24 md:px-12"
		>
			<div className="mx-auto max-w-[1200px]">
				<SectionHeader
					headline="Save "
					headlineItalic="anything."
					description="Articles, PDFs, YouTube videos, podcasts, your own notes. Clearvoid processes each one and makes it available for AI conversation alongside everything else."
				/>
				<ScaledMock>
					<div className="border-cv-bdr bg-cv-bg-card overflow-hidden rounded-xl border shadow-[0_0_0_1px_rgba(255,255,255,0.04)_inset]">
						<div className="border-cv-bdr flex h-11 shrink-0 items-center border-b px-6">
							<span className="text-cv-fg font-serif text-lg">
								Library
							</span>
						</div>
						<div className="border-cv-bdr flex h-9 shrink-0 items-center gap-5 border-b px-6">
							{["All", "Documents", "Articles", "Videos"].map(
								(tab, i) => (
									<span
										key={tab}
										className={`pb-1.5 text-[11px] ${
											i === 0
												? "border-cv-accent text-cv-fg border-b"
												: "text-cv-fg-muted"
										}`}
									>
										{tab}
									</span>
								),
							)}
							<div className="ml-auto">
								<input
									className="border-cv-bdr bg-cv-bg-elevated text-cv-fg-sec placeholder:text-cv-fg-muted w-[180px] rounded border px-2.5 py-1 text-[11px] outline-none"
									placeholder="Filter..."
									readOnly
								/>
							</div>
						</div>
						<LibraryItem
							icon="video"
							title="YC: How to Price Your Startup (2024)"
							summary="Fetching transcript and generating summary..."
							badge="Video"
							badgeColor="video"
							date=""
							processing
						/>
						<LibraryItem
							icon="link"
							title="The Aggregation Theory of Pricing — Stratechery"
							summary="Ben Thompson argues that aggregators capture value through user relationships, not supply relationships..."
							badge="Article"
							badgeColor="article"
							date="2 hours ago"
						/>
						<LibraryItem
							icon="doc"
							title="Profitwell: SaaS Pricing Report 2025.pdf"
							summary="Survey of 2,400 SaaS companies on pricing models, churn rates, and expansion revenue..."
							badge="PDF"
							badgeColor="plan"
							date="Yesterday"
						/>
						<LibraryItem
							icon="doc"
							title="My note: Elicit and the credit model experiment"
							summary="Elicit switched from flat to credit-based in late 2023. Initial churn spike but expansion revenue tripled..."
							badge="Note"
							badgeColor="research"
							date="3 days ago"
						/>
					</div>
				</ScaledMock>
			</div>
		</section>
	);
}

// ── Section: Feature 2 — Chat about it ──────────────────────

function FeatureChat() {
	return (
		<section className="border-b border-[rgba(255,255,255,0.08)] px-6 py-24 md:px-12">
			<div className="mx-auto max-w-[1200px]">
				<SectionHeader
					headline="Chat about "
					headlineItalic="it."
					description="Ask questions across all your saved sources at once. Every answer is grounded in your research, with citations you can trace back."
				/>
				<ScaledMock>
					<div className="border-cv-bdr bg-cv-bg-card overflow-hidden rounded-xl border shadow-[0_0_0_1px_rgba(255,255,255,0.04)_inset]">
						<div className="flex h-[520px]">
							<div className="border-cv-bdr flex min-w-0 flex-1 flex-col border-r">
								<DocumentBreadcrumb title="Pricing Strategy Notes" />
								<DocumentTabs />
								<div className="flex-1 overflow-hidden px-8 py-6">
									<div className="text-cv-fg-muted mb-2 text-[10px] font-medium tracking-[0.1em] uppercase">
										Research Document
									</div>
									<div className="text-cv-fg mb-3 font-serif text-xl">
										Pricing Strategy Notes
									</div>
									<div className="text-cv-fg-sec mb-4 text-xs leading-relaxed">
										Reviewing a pattern across several
										sources: companies that move to
										usage-based pricing tend to see initial
										churn spikes followed by stronger net
										revenue retention. The mechanism seems
										to be that high-value users stop
										churning when the price reflects their
										actual usage...
									</div>
									<div className="border-cv-accent bg-cv-accent-glow mb-4 rounded-r border-l-2 px-4 py-3">
										<div className="text-cv-fg-sec text-xs leading-relaxed">
											The core insight from the Profitwell
											report: usage-based companies grow
											38% faster not because they acquire
											more customers, but because they
											retain and expand existing ones at a
											higher rate.
										</div>
									</div>
									<div className="text-cv-fg-sec mb-4 text-xs leading-relaxed">
										This tracks with what Elicit did in 2023
										— credit model, initial churn spike,
										then tripling of expansion revenue.
										Worth investigating whether the spike is
										a real risk or a measurement artifact...
									</div>
									<div className="text-cv-fg-muted mt-auto pt-4 text-[10px]">
										7 sources in context
									</div>
								</div>
							</div>
							<div className="w-[340px]">
								<ChatPanel sourceCount={7}>
									<ChatMessage
										role="user"
										content="Is the churn spike after switching to usage-based a real risk or just a measurement artifact?"
									/>
									<ChatMessage
										role="ai"
										content={
											<>
												Your sources suggest it is real
												but short-lived.
												<sup className="text-cv-accent text-[8px] font-bold">
													1
												</sup>{" "}
												The Profitwell data shows a
												median 14% churn increase in the
												first 90 days, but net revenue
												retention turns positive by day
												60.
												<sup className="text-cv-accent text-[8px] font-bold">
													2
												</sup>{" "}
												Your Elicit note matches this:
												the spike resolved and expansion
												revenue tripled.
												<sup className="text-cv-accent text-[8px] font-bold">
													3
												</sup>
											</>
										}
										citations={CHAT_CITATIONS}
									/>
								</ChatPanel>
							</div>
						</div>
					</div>
				</ScaledMock>
			</div>
		</section>
	);
}

// ── Section: Feature 3 — Build on it ────────────────────────

function FeatureBuild() {
	return (
		<section className="border-b border-[rgba(255,255,255,0.08)] px-6 py-24 md:px-12">
			<div className="mx-auto max-w-[1200px]">
				<SectionHeader
					headline="Build on "
					headlineItalic="it."
					description="Turn research into documents that stay connected to their sources. Mention any saved item inline and the AI can suggest where your evidence fits."
				/>
				<ScaledMock>
					<div className="border-cv-bdr bg-cv-bg-card overflow-hidden rounded-xl border shadow-[0_0_0_1px_rgba(255,255,255,0.04)_inset]">
						<div className="px-10 py-8">
							<div className="text-cv-fg mb-1 font-serif text-xl">
								Product Vision — Q2 2026
							</div>
							<div className="text-cv-fg-muted mb-6 text-[11px]">
								Draft · Last edited just now
							</div>
							<div className="text-cv-fg-sec mb-5 text-[13px] leading-[1.85]">
								The strategic case for launching a usage-based
								tier rests on two findings from the research.
								First, the pattern documented in{" "}
								<DocumentMention title="Profitwell Report 2025" />{" "}
								— usage-based SaaS companies grow 38% faster not
								from acquisition but from retention and
								expansion of high-value users.
							</div>
							<div className="text-cv-fg-sec mb-5 text-[13px] leading-[1.85]">
								Second, the mechanism is visible in{" "}
								<DocumentMention title="Elicit analysis note" />{" "}
								: the initial churn spike after switching is
								real but resolves within 60 days, and the
								ceiling on net revenue retention rises
								substantially.
							</div>
							<div className="border-cv-bdr-mid border-l-cv-accent bg-cv-bg-elevated my-6 rounded-r-md border border-l-[3px] px-5 py-4">
								<div className="text-cv-accent mb-2 text-[9px] font-semibold tracking-[0.1em] uppercase">
									AI Suggestion
								</div>
								<div className="text-cv-fg-sec text-[13px] leading-relaxed">
									Your sources suggest a phased rollout
									reduces churn risk: introduce usage-based as
									an opt-in tier, track expansion revenue for
									60 days, then sunset flat pricing. Consider
									citing the{" "}
									<DocumentMention title="YC Pricing Workshop" />{" "}
									framing here.
								</div>
							</div>
							<div className="text-cv-fg-sec text-[13px] leading-[1.85]">
								The open question is whether Clearvoid's user
								base skews toward researchers with predictable
								usage or exploratory users with highly variable
								usage. This shapes which tier should be the
								default.
								<span className="bg-cv-accent ml-0.5 inline-block h-3.5 w-0.5 animate-pulse align-middle opacity-80" />
							</div>
						</div>
					</div>
				</ScaledMock>
			</div>
		</section>
	);
}

// ── Section: Bottom CTA ─────────────────────────────────────

function BottomCta() {
	return (
		<section className="px-6 py-32 text-center md:px-12">
			<div className="mx-auto max-w-[600px]">
				<h2 className="mb-6 font-serif text-[clamp(32px,4vw,48px)] leading-[1.1] text-[#e8e8e8]">
					Start building your research{" "}
					<span className="text-[#a0a0a8] italic">library.</span>
				</h2>
				<p className="mb-8 text-base leading-relaxed text-[#a0a0a8]">
					Every article, video, and note you save makes the next
					conversation smarter. Your library compounds — start now.
				</p>
				<div className="flex flex-col items-center gap-3">
					<SoonButton className="bg-cv-accent rounded-md px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90" />
				</div>
			</div>
		</section>
	);
}

// ── Section: Footer ─────────────────────────────────────────

function Footer() {
	return (
		<footer className="border-t border-[rgba(255,255,255,0.08)] px-6 py-8 md:px-12">
			<div className="mx-auto flex max-w-[1200px] items-center justify-between">
				<span className="text-xs text-[#606068]">
					&copy; 2026 Baseline Reset
				</span>
				<div className="flex items-center gap-5">
					{/* <a
						href="/privacy"
						className="text-xs text-[#606068] transition-colors hover:text-[#a0a0a8]"
					>
						Privacy
					</a>
					<a
						href="/terms"
						className="text-xs text-[#606068] transition-colors hover:text-[#a0a0a8]"
					>
						Terms
					</a> */}
					{/* <a
						href="mailto:hello@baselinereset.com"
						className="text-xs text-[#606068] transition-colors hover:text-[#a0a0a8]"
					>
						Contact
					</a> */}
				</div>
			</div>
		</footer>
	);
}

// ── Main landing page ───────────────────────────────────────

export default function LandingPage() {
	return (
		<div
			className="min-h-screen bg-[#09090b] pb-[env(safe-area-inset-bottom)] text-[#e8e8e8]"
			style={{
				backgroundImage:
					"radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59,130,246,0.08), transparent)",
			}}
		>
			<Nav />
			<Hero />
			<Statement />
			<FeatureSave />
			<FeatureChat />
			<FeatureBuild />
			<BottomCta />
			<Footer />
		</div>
	);
}
